import React, { useEffect, useState, useCallback } from "react";
import CabRequestServices from "../services/externalServices/cabRequestServices";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  TablePagination,
  Card,
  CardContent,
  CardHeader,
  Paper,
  Box,
  CircularProgress,
} from "@mui/material";

// Function Component
const CabRequestDetails = ({ id }) => {
  const [cabRequests, setCabRequests] = useState([]);
  const [loading, setLoading] = useState(true); // New state for loader
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [totalCount, setTotalCount] = useState(0); // New state for total count

  // Memoize the getCabRequests function using useCallback to avoid unnecessary re-creation
  const getCabRequests = useCallback(async () => {
    try {
      setLoading(true); // Start loading
      const res = await CabRequestServices.getUserCabRequestById(
        id,
        page,
        rowsPerPage
      );
      console.log("response in cab request", res.totalCount);
      setCabRequests(res.cabRequestsWithBids); // Assuming response has a 'data' field
      setTotalCount(res.totalCount); // Assuming response has a 'totalCount' field for pagination
    } catch (error) {
      console.error("Error while getting the cab request by id", error);
    } finally {
      setLoading(false); // Stop loading
    }
  }, [id, page, rowsPerPage]);

  // Trigger data fetch on page or rowsPerPage change
  useEffect(() => {
    getCabRequests();
  }, [getCabRequests]); // useCallback makes getCabRequests stable

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0); // Reset page to 0 when rows per page is changed
  };

  return (
    <div style={{ padding: "10px" }}>
      {loading ? (
        // Show loader while loading
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="10vh"
        >
          <CircularProgress />
        </Box>
      ) : cabRequests?.length === 0 ? (
        // Show message when no cab requests are found
        <Typography variant="h6" textAlign="center">
          No cab requests found
        </Typography>
      ) : (
        cabRequests?.map((request) => (
          <Card key={request.cabRequestId} style={{ marginBottom: "20px" }}>
            <CardHeader
              title={
                <Box textAlign="center" sx={{ color: "grey", font: "bold" }}>
                  Package Title: {request.packageTitle}
                </Box>
              }
              sx={{
                padding: "16px",
                backgroundColor: "#f5f5f5",
              }}
            />
            <CardContent>
              {request.bids.length === 0 ? (
                <Typography variant="body2" color="textSecondary">
                  No bids available for this cab request.
                </Typography>
              ) : (
                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Bid ID</TableCell>
                        <TableCell>Service</TableCell>
                        <TableCell>Rider Name</TableCell>
                        <TableCell>Rider Phone</TableCell>
                        <TableCell>Amount</TableCell>
                        <TableCell>Rider Fee</TableCell>
                        <TableCell>Base Amount</TableCell>
                        <TableCell>Final Amount</TableCell>
                        <TableCell>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {request.bids?.map((bid, index) => (
                        <TableRow key={`${bid._id || index}`}>
                          <TableCell>{bid?._id}</TableCell>
                          <TableCell>{bid?.serviceAssociated}</TableCell>
                          <TableCell>{bid.rider?.first_name}</TableCell>
                          <TableCell>{bid.rider?.phone_number}</TableCell>
                          <TableCell>₹{bid?.amount}</TableCell>
                          <TableCell>₹{bid?.riderFee}</TableCell>
                          <TableCell>
                            ₹{bid.pricing?.baseAmount?.value}
                          </TableCell>
                          <TableCell>₹{bid.pricing?.finalAmount}</TableCell>
                          <TableCell>
                            <Typography
                              style={{
                                color: bid.isAccepted ? "green" : "red",
                                fontWeight: "bold",
                              }}
                            >
                              {bid.isAccepted ? "Accepted" : "Rejected"}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        ))
      )}
      {!loading && cabRequests?.length > 0 && (
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={totalCount} // Set the total count of records for pagination
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      )}
    </div>
  );
};

// Memoize the component
export default React.memo(CabRequestDetails);
